// SearchableSelect.jsx - A performant searchable dropdown for large option lists
import React, { useState, useRef, useEffect, useMemo } from "react";

/**
 * SearchableSelect - A dropdown that filters options as you type
 * Optimized for large lists by only rendering filtered options
 *
 * Props:
 *   onRequestSite {fn} - if provided and typed value is not in options,
 *     a "Request site" row appears; called with (siteName: string)
 */
export default function SearchableSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select...",
  className = "form-select form-select-sm",
  style = {},
  allowCustom = false,
  onRequestSite = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState(value || "");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find the selected option label
  const selectedOption = useMemo(() => {
    return options.find((opt) => opt.value === value) || null;
  }, [options, value]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      // Show all options, but limit to first 100 for performance
      return options.slice(0, 100);
    }
    const term = searchTerm.toLowerCase();
    return options.filter((opt) => {
      const label = (opt.label || opt.value || "").toLowerCase();
      return label.includes(term);
    });
  }, [options, searchTerm]);

  const termIsExactMatch = useMemo(() => {
    if (!searchTerm) return false;
    return filteredOptions.some(
      (opt) =>
        String(opt.value || "").toLowerCase() === searchTerm.toLowerCase()
    );
  }, [filteredOptions, searchTerm]);

  const showRequestRow = Boolean(
    onRequestSite &&
    searchTerm.trim() &&
    !termIsExactMatch
  );

  // Update input value when value prop changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || "");
      setSearchTerm("");
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // For position: fixed, use viewport coordinates directly (no scroll offset needed)
        setDropdownPosition({
          top: rect.bottom + 2,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    // Update position immediately
    updatePosition();

    // Listen to scroll events on window and all scrollable parents
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    
    // Also listen to scroll on document and body
    document.addEventListener("scroll", updatePosition, true);
    document.body.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
      document.body.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    if (newIsOpen && containerRef.current) {
      // Calculate position for fixed dropdown (viewport coordinates for position: fixed)
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
      setSearchTerm("");
    } else {
      setSearchTerm("");
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
    setInputValue(optionValue);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
    
    // If allowCustom, update the parent immediately
    if (allowCustom) {
      onChange(newValue);
    }
  };

  const handleInputBlur = () => {
    // Don't close if clicking on an option
    setTimeout(() => {
      if (allowCustom && inputValue && !selectedOption) {
        onChange(inputValue);
      }
      setIsOpen(false);
    }, 200);
  };

  const handleRequestSite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const site = searchTerm.trim();
    if (!site) return;
    setIsOpen(false);
    setSearchTerm("");
    onRequestSite(site);
  };

  const displayValue = isOpen ? inputValue : (selectedOption?.label || selectedOption?.value || value || "");

  return (
    <div 
      ref={containerRef} 
      className="position-relative" 
      style={{ 
        width: "100%",
        zIndex: isOpen ? 9999 : "auto",
      }}
    >
      <div
        className={`${className} d-flex align-items-center`}
        style={{
          ...style,
          cursor: "pointer",
          position: "relative",
          minHeight: "31px",
        }}
        onClick={handleToggle}
      >
        {!isOpen ? (
          <>
            <span className="flex-grow-1 text-truncate" style={{ fontSize: "0.875rem" }}>
              {displayValue || <span className="text-muted">{placeholder}{allowCustom && <span className="text-primary"> (click to add new)</span>}</span>}
            </span>
            <span className="ms-2" style={{ fontSize: "0.7rem", opacity: 0.6 }}>▼</span>
          </>
        ) : (
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm border-0 p-0"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onClick={(e) => e.stopPropagation()}
            placeholder={allowCustom ? `${placeholder} (type to add new)` : placeholder}
            style={{
              fontSize: "0.875rem",
              width: "100%",
              background: "transparent",
            }}
            autoFocus
          />
        )}
      </div>

      {isOpen && (
        <>
          {/* Backdrop to capture clicks outside - transparent */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              background: "transparent",
            }}
            onClick={() => {
              setIsOpen(false);
              setSearchTerm("");
            }}
          />
          {/* Dropdown menu with fixed positioning */}
          <div
            ref={dropdownRef}
            className="bg-white border rounded shadow-lg"
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 9999,
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
          {filteredOptions.length === 0 && !showRequestRow ? (
            <div className="p-2 text-muted small">
              {allowCustom ? (
                <>
                  No matches found. <strong>Type to create a new site</strong>
                </>
              ) : (
                "No matches found"
              )}
            </div>
          ) : (
            <>
              {filteredOptions.map((option) => {
                const optValue = option.value || option;
                const optLabel = option.label || option.value || option;
                const isSelected = value === optValue;
                return (
                  <div
                    key={optValue}
                    className={`p-2 ${isSelected ? "bg-primary text-white" : ""}`}
                    style={{
                      cursor: "pointer",
                      fontSize: "0.875rem",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(optValue);
                    }}
                  >
                    {optLabel}
                  </div>
                );
              })}
              {filteredOptions.length >= 100 && !searchTerm && (
                <div className="p-2 text-muted small border-top">
                  Showing first 100 options. Type to search for more...
                  {allowCustom && <><br /><strong>Or type a new site name to create it</strong></>}
                </div>
              )}
              {allowCustom && searchTerm && !filteredOptions.some(opt => {
                const optValue = opt.value || opt;
                return String(optValue).toLowerCase() === searchTerm.toLowerCase();
              }) && (
                <div className="p-2 text-primary small border-top" style={{ backgroundColor: "#e7f3ff" }}>
                  <strong>Press Enter or click outside to create: "{searchTerm}"</strong>
                </div>
              )}

              {showRequestRow && (
                <div
                  className="p-2 small border-top d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#fff8e1",
                    cursor: "pointer",
                    color: "#856404",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff3cd")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#fff8e1")
                  }
                  onMouseDown={handleRequestSite}
                >
                  <span style={{ fontSize: "1rem" }}>📨</span>
                  <span>
                    <strong>Request new site:</strong> "{searchTerm}"
                  </span>
                </div>
              )}
            </>
          )}
          </div>
        </>
      )}
    </div>
  );
}

